import { zodResolver } from "@hookform/resolvers/zod";
import Decimal from "decimal.js";
import i18next from "i18next";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useDeepCompareEffect from "use-deep-compare-effect";
import { z } from "zod";

import { type DraftPayment, DraftPaymentStatus } from "@/apis/gql/types";
import { AutoSaveForm } from "@/components/shared/form/form-auto-save";
import { FormNumberInput } from "@/components/shared/form/form-number-input";
import {
  ValidationErrorMessage,
  ValidationRoot,
} from "@/components/shared/form/shared/form-validation";
import { Form as FormProvider } from "@/components/ui/form";
import { formatCurrencyValue, getCurrencySymbol } from "@/lib/utils";
import {
  INVOICE_QUERY,
  type InvoiceDetailsDraftPayment,
} from "@/queries/invoices/get";
import { useCreateDraftPayment } from "@/queries/payments/draft-payments/create-draft-payment";
import { useUpdateDraftPayment } from "@/queries/payments/draft-payments/update-draft-payment";

type Props = {
  draftPayments: InvoiceDetailsDraftPayment[];
  completedDraftPayments: InvoiceDetailsDraftPayment[];
  invoiceId: string;
  currency: string;
  totalAmount: number;
  onValuesChange?: (values: PaymentFormValues) => void;
};

export const PartialPaymentsForm = ({
  draftPayments,
  completedDraftPayments,
  invoiceId,
  currency,
  totalAmount = 0,
  onValuesChange,
}: Props) => {
  const [createDraftPayment] = useCreateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });
  const { updateDraftPayment } = useUpdateDraftPayment({
    refetchQueries: [INVOICE_QUERY],
  });

  const handleCreatePayment = (values: PaymentFormValues) => {
    createDraftPayment(invoiceId, {
      rawAmount: values.rawAmount,
      currency: values.currency,
    });
  };

  const handleUpdatePayment = (values: PaymentFormValues) => {
    updateDraftPayment({
      draftPaymentId: values.draftPaymentId,
      rawAmount: values.rawAmount,
      currency: values.currency,
    });
  };

  const totalPaidAmount = completedDraftPayments
    .filter(
      (payment) =>
        payment.status &&
        [DraftPaymentStatus.INITIATED].includes(payment.status),
    )
    .reduce((acc, payment) => acc + (payment.amount ?? 0), 0);

  if (draftPayments.length === 0) {
    return (
      <Form
        values={{
          currency,
          rawAmount: 0,
        }}
        onSubmit={handleCreatePayment}
        totalAmount={totalAmount}
        totalPaidAmount={totalPaidAmount}
        onValuesChange={onValuesChange}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {draftPayments.map((payment, index) => (
        <Form
          key={payment.draftPaymentId}
          index={index + 1}
          values={{
            ...payment,
            currency,
          }}
          onSubmit={handleUpdatePayment}
          totalAmount={totalAmount}
          totalPaidAmount={totalPaidAmount}
          onValuesChange={onValuesChange}
        />
      ))}
    </div>
  );
};

const paymentWarningFormSchema = z
  .object({
    bankTxnProjectionId: z.string().optional().nullable(),
    bankTxnId: z.string().optional(),
    amount: z.number().optional().nullable(),
    rawAmount: z.number().optional().nullable(),
    currency: z.string().optional().nullable(),
    totalAmount: z.number().optional().nullable(),
  })
  .refine(
    (data) => {
      const paymentAmount = new Decimal(data.rawAmount ?? 0);
      const totalAmount = new Decimal(data.totalAmount ?? 0);

      return paymentAmount.lte(totalAmount);
    },
    {
      message: i18next.t("payment_validation_error.amount.exceeds_total"),
      path: ["rawAmount"],
    },
  );

const paymentFormSchema = z.object({
  draftPaymentId: z.string().optional(),
  amount: z.number().optional().nullable(),
  rawAmount: z.number().optional().nullable(),
  currency: z.string().optional().nullable(),
});

type PaymentFormValues = z.infer<typeof paymentFormSchema>;

type FormProps = {
  values: PaymentFormValues;
  index?: number;
  disabled?: boolean;
  onSubmit: (values: PaymentFormValues) => void;
  totalAmount: number;
  totalPaidAmount: number;
  onValuesChange?: (values: PaymentFormValues) => void;
};

const Form = ({
  values,
  disabled = false,
  onSubmit,
  index,
  totalAmount,
  totalPaidAmount,
  onValuesChange,
}: FormProps) => {
  const { t } = useTranslation();

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    values,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const formValues = form.watch();

  const isDirty = form.formState.isDirty;

  const currencySymbol = getCurrencySymbol(formValues.currency ?? "EUR");

  const handleSubmit = (values: PaymentFormValues) => {
    onSubmit(values);
  };

  const remainingAmount = new Decimal(totalAmount ?? 0)
    .sub(totalPaidAmount)
    .sub(formValues.amount ?? 0);

  useDeepCompareEffect(() => {
    if (isDirty) {
      onValuesChange?.(formValues);
    }
  }, [formValues, onValuesChange, isDirty]);

  console.log("totalPaidAmount", formValues.rawAmount);

  return (
    <ValidationRoot
      schema={paymentWarningFormSchema}
      values={{ ...formValues, totalAmount }}
    >
      <FormProvider {...form}>
        <form className="grid grid-cols-2 gap-2">
          <>
            <div className="col-span-2">
              <FormNumberInput
                label={t("payment_details.fields.amount")}
                name="rawAmount"
                disabled={disabled}
                suffix={` ${currencySymbol}`}
                description={t("payment_details.fields.amount_description", {
                  amount: `${formatCurrencyValue(
                    remainingAmount.toNumber(),
                  )} ${getCurrencySymbol(values.currency ?? "EUR")}`,
                })}
                disableFastCentInput
              />
              <ValidationErrorMessage name="rawAmount" />
            </div>
          </>
          <AutoSaveForm onSubmit={handleSubmit} immediate />
        </form>
      </FormProvider>
    </ValidationRoot>
  );
};

export type PartialPaymentsFormValues = PaymentFormValues;
